import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
const safeUrl = (value, image = false) => {
    if (typeof value !== "string")
        return undefined;
    try {
        const url = new URL(value, window.location.origin);
        if (url.protocol === "https:" ||
            (url.protocol === "http:" && location.hostname === "localhost") ||
            (image && url.protocol === "data:" && value.startsWith("data:image/")))
            return value;
    }
    catch {
        return undefined;
    }
};
function Inline({ node }) {
    let child = node.text ?? "";
    for (const mark of node.marks ?? []) {
        if (mark.type === "bold")
            child = _jsx("strong", { children: child });
        if (mark.type === "italic")
            child = _jsx("em", { children: child });
        if (mark.type === "underline")
            child = _jsx("u", { children: child });
        if (mark.type === "link")
            child = (_jsx("a", { href: safeUrl(mark.attrs?.href), rel: "noopener noreferrer", children: child }));
    }
    return _jsx(_Fragment, { children: child });
}
function NodeView({ node, index }) {
    const children = (node.content ?? []).map((child, i) => child.type === "text" ? (_jsx(Inline, { node: child }, i)) : (_jsx(NodeView, { node: child, index: i }, i)));
    const key = `${node.type}-${index}`;
    switch (node.type) {
        case "doc":
            return _jsx(_Fragment, { children: children });
        case "paragraph":
            return _jsx("p", { children: children }, key);
        case "heading": {
            const level = Math.min(4, Math.max(2, Number(node.attrs?.level ?? 2)));
            return React.createElement(`h${level}`, { key }, children);
        }
        case "bulletList":
            return _jsx("ul", { children: children }, key);
        case "orderedList":
            return _jsx("ol", { children: children }, key);
        case "listItem":
            return _jsx("li", { children: children }, key);
        case "blockquote":
            return _jsx("blockquote", { children: children }, key);
        case "horizontalRule":
            return _jsx("hr", {}, key);
        case "hardBreak":
            return _jsx("br", {}, key);
        case "scriptureCallout":
            return (_jsxs("aside", { className: "scripture", "aria-label": "Scripture callout", children: [node.attrs?.reference ? (_jsx("strong", { children: String(node.attrs.reference) })) : null, children] }, key));
        case "responsiveColumns":
            return (_jsx("div", { className: `rich-columns columns-${Math.min(3, Math.max(2, Number(node.attrs?.count ?? 2)))}`, children: children }, key));
        case "column":
            return (_jsx("section", { className: "rich-column", children: children }, key));
        case "image": {
            const src = safeUrl(node.attrs?.src, true);
            return src ? (_jsxs("figure", { children: [_jsx("img", { src: src, alt: String(node.attrs?.alt ?? ""), loading: "lazy" }), node.attrs?.caption ? (_jsx("figcaption", { children: String(node.attrs.caption) })) : null] }, key)) : null;
        }
        case "videoEmbed": {
            const src = safeUrl(node.attrs?.src);
            return src ? (_jsx("div", { className: "video-wrap", children: _jsx("iframe", { src: src, title: String(node.attrs?.title ?? "Embedded video"), loading: "lazy", allow: "fullscreen" }) }, key)) : null;
        }
        case "table":
            return (_jsx("div", { className: "table-scroll", role: "region", "aria-label": String(node.attrs?.description ?? "Data table"), tabIndex: 0, children: _jsx("table", { children: _jsx("tbody", { children: children }) }) }, key));
        case "tableRow":
            return _jsx("tr", { children: children }, key);
        case "tableHeader":
            return _jsx("th", { children: children }, key);
        case "tableCell":
            return _jsx("td", { children: children }, key);
        case "spacer":
            return (_jsx("div", { style: { height: Math.min(96, Number(node.attrs?.height ?? 24)) }, "aria-hidden": "true" }, key));
        default:
            return _jsx(_Fragment, { children: children });
    }
}
export function RichDocument({ document }) {
    return (_jsx("div", { className: "rich-document", children: _jsx(NodeView, { node: document, index: 0 }) }));
}
